// ── Photo Gallery with lightbox ──

import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { PhotoResolved } from "../types/graph";

interface PhotoGalleryProps {
  photos: PhotoResolved[];
}

export default function PhotoGallery({ photos }: PhotoGalleryProps) {
  const [selected, setSelected] = useState<PhotoResolved | null>(null);

  const close = useCallback(() => setSelected(null), []);

  return (
    <>
      {/* Thumbnail grid */}
      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo) => (
          <button
            key={photo.photoId}
            onClick={() => setSelected(photo)}
            className="group relative aspect-square overflow-hidden rounded-lg
                       bg-fl-sand-40 border border-fl-evergreen-20
                       hover:border-fl-leaf transition-all duration-200"
          >
            <img
              src={photo.thumbnail}
              alt="Inspection photo"
              loading="lazy"
              className="w-full h-full object-cover
                         group-hover:scale-110 transition-transform duration-300"
              onError={(e) => {
                // Fallback if thumbnail fails
                (e.target as HTMLImageElement).src = photo.url;
              }}
            />
            <div
              className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent
                          opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md"
            onClick={close}
          >
            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              src={selected.large}
              alt="Inspection photo"
              className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
              onError={(e) => {
                (e.target as HTMLImageElement).src = selected.url;
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={close}
              className="absolute top-6 right-6 text-white/60 hover:text-white
                         text-2xl w-10 h-10 flex items-center justify-center
                         rounded-full bg-fl-evergreen/60 hover:bg-fl-evergreen transition-colors"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
